import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody, Container } from "reactstrap";
import { Spinner } from "../../../AbstractElements";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  getDriverSchemeDetails
} from "../../../Services/Driver.service";
import SharedDataContext from "../../../_helper/SharedData";
import { showAlert } from "../../../Services/Shared.service";
import TariffModal from "../../../CommonElements/TariffModal/TariffModal";
import UnifiedDriverSchemeForm from "../Forms/UnifiedDriverSchemeForm";
import BulkDeductionForm from "../Forms/BulkDeductionForm";
import DeductionList from "./DeductionList";
import editIcon from "../../../assets/images/tarrifManagement/edit.png";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Box, Typography, Tooltip, IconButton, Button } from "@mui/material";

const DriverSchemeDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { cityData } = useContext(SharedDataContext);

  const [schemeData, setSchemeData] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [loader, setLoader] = useState(true);
  const [isEditModal, setIsEditModal] = useState(false);
  const [isAddDeductionModal, setIsAddDeductionModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    setLoader(true);
    try {
      const resp = await getDriverSchemeDetails(id);

      if (resp.status === 200) {
        const data = resp.data;

        // Map the combined DTO back to the components expected structures
        setSchemeData({
          ...data,
          id: data.driverTypeId,
          name: data.driverTypeName,
          serviceCityId: data.serviceCityId
        });

        // Map wallet details if present
        if (data.id) { // If id is present, it's the wallet balance ID
          setWalletData({
            minimumBalance: data.minimumBalance,
            applicableFrom: data.applicableFrom,
            applicableTo: data.applicableTo,
            id: data.id
          });
        } else {
          setWalletData(null);
        }
      }
    } catch (error) {
      console.error("Error fetching combined details:", error);
      showAlert("error", "Failed to fetch details");
    } finally {
      setLoader(false);
    }
  };

  const triggerTableRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (loader && !schemeData) {
    return (
      <div className="loader-box" style={{ height: "80vh" }}>
        <Spinner attrSpinner={{ className: "loader-3" }} />
      </div>
    );
  }

  const cityName = cityData.find(c => c.id === schemeData?.serviceCityId)?.label || "Unknown City";

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "left" }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIosIcon />}
          sx={{
            color: "#41CC7D",
            marginBottom: 2,
            textTransform: "none",
            "&:hover": { backgroundColor: "transparent", color: "#39B54A" }
          }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </div>

      <Container fluid={true} className="p-0">
        <Row>
          <Col sm="12">
            <Card className="clientInfoCard">
              <CardBody
                style={{
                  backgroundColor: "rgba(20, 152, 38, 0.9)",
                  borderRadius: "10px",
                  boxShadow: `inset 0 0 8px white,inset 0 0 4px white`,
                  border: "1px solid rgba(116, 206, 106, 1)",
                  padding: "25px"
                }}
              >
                <Box sx={{ marginX: "10px" }}>
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
                        <Typography sx={{ fontSize: "1.67vw", color: "#ffffff", fontWeight: "600" }}>
                          {schemeData?.name}
                        </Typography>
                        <Box sx={{
                          display: "flex",
                          backgroundColor: "#E3F5E1",
                          height: "2.01vw",
                          justifyContent: "space-evenly",
                          alignItems: "center",
                          borderRadius: "6px",
                          padding: "0 12px",
                          border: "2px solid rgba(116, 206, 106, 1)",
                          gap: "8px"
                        }}>
                          <Box sx={{ width: "0.56vw", height: "0.56vw", background: "rgba(116, 206, 106, 1)", borderRadius: "50%" }} />
                          <Typography sx={{ fontSize: "0.97vw", fontWeight: "600" }}>Active</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <img
                          src={editIcon}
                          alt="edit"
                          style={{ width: "1.75vw", height: "1.61vw", cursor: "pointer" }}
                          onClick={() => setIsEditModal(true)}
                        />
                        <Tooltip title="Add Deduction">
                          <IconButton size="large" onClick={() => setIsAddDeductionModal(true)}>
                            <AddCircleIcon sx={{ color: "#FFFF", width: "2.08vw", height: "2.08vw" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ width: "80%", marginTop: "30px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box sx={{ flex: "1" }}>
                        <Typography sx={{ fontWeight: "400", fontSize: "0.97vw", color: "white" }}>Service City</Typography>
                        <Typography sx={{ fontWeight: "600", fontSize: "0.97vw", color: "white" }}>{cityName}</Typography>
                      </Box>
                      <Box sx={{ flex: "1" }}>
                        <Typography sx={{ fontWeight: "400", fontSize: "0.97vw", color: "white" }}>Subscription</Typography>
                        <Typography sx={{ fontWeight: "600", fontSize: "0.97vw", color: "white" }}>{schemeData?.isSubscription ? "Yes" : "No"}</Typography>
                      </Box>
                      <Box sx={{ flex: "1" }}>
                        <Typography sx={{ fontWeight: "400", fontSize: "0.97vw", color: "white" }}>Hidden</Typography>
                        <Typography sx={{ fontWeight: "600", fontSize: "0.97vw", color: "white" }}>{schemeData?.isHide ? "Yes" : "No"}</Typography>
                      </Box>
                      <Box sx={{ flex: "1" }}>
                        <Typography sx={{ fontWeight: "400", fontSize: "0.97vw", color: "white" }}>Min Balance</Typography>
                        <Typography sx={{ fontWeight: "600", fontSize: "0.97vw", color: "white" }}>₹{walletData?.minimumBalance || 0}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "30px" }}>
                      <Box sx={{ flex: "1" }}>
                        <Typography sx={{ fontWeight: "400", fontSize: "0.97vw", color: "white" }}>Valid From</Typography>
                        <Typography sx={{ fontWeight: "600", fontSize: "0.97vw", color: "white" }}>
                          {walletData?.applicableFrom ? new Date(walletData.applicableFrom).toLocaleDateString() : "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: "1" }}>
                        <Typography sx={{ fontWeight: "400", fontSize: "0.97vw", color: "white" }}>Valid To</Typography>
                        <Typography sx={{ fontWeight: "600", fontSize: "0.97vw", color: "white" }}>
                          {walletData?.applicableTo ? new Date(walletData.applicableTo).toLocaleDateString() : "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: "1" }}>
                        <Typography sx={{ fontWeight: "400", fontSize: "0.97vw", color: "white" }}>Created On</Typography>
                        <Typography sx={{ fontWeight: "600", fontSize: "0.97vw", color: "white" }}>
                          {schemeData?.createdOn ? new Date(schemeData.createdOn).toLocaleDateString() : "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: "1" }}></Box>
                    </Box>
                  </Box>
                </Box>
              </CardBody>
            </Card>

            {/* Modular Deduction List */}
            <DeductionList 
              scheme={schemeData} 
              refreshTrigger={refreshTrigger} 
            />
          </Col>
        </Row>
      </Container>

      <TariffModal
        isOpen={isEditModal}
        size="lg"
        centered
        toggler={() => setIsEditModal(false)}
        title="Edit Driver Scheme"
      >
        <UnifiedDriverSchemeForm
          editId={id}
          setIsModal={setIsEditModal}
          triggerRefresh={fetchDetails}
        />
      </TariffModal>

      <TariffModal
        isOpen={isAddDeductionModal}
        size="xl"
        centered
        toggler={() => setIsAddDeductionModal(false)}
        title="Bulk Add Deductions"
      >
        <BulkDeductionForm 
          driverScheme={schemeData}
          onClose={() => setIsAddDeductionModal(false)}
          onRefresh={triggerTableRefresh}
        />
      </TariffModal>
    </div>
  );
};

export default DriverSchemeDetailView;
